import React, { useEffect, useRef, useState } from 'react';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetCurrencyQuery } from '@entities/currency/api/hooks/useGetCurrencyQuery';
import { createChild } from '@features/CreateEstimateModal/api/createChild';
import { IFormValues } from '@features/CreateEstimateModal/model/types';
import { useHandleForm } from '@shared/hooks';
import { CustomModal, Flex, Form, Input, Select, UiButton, UiText } from '@shared/ui';

import cls from './CreateEstimateModal.module.css';
import { useCreateEstimateModal } from '../lib/hooks/useCreateEstimateModal';

interface CreateEstimateModalProps {
    isOpen: boolean;
    handleClose: () => void;
}

export const CreateEstimateModal = ({ isOpen, handleClose }: CreateEstimateModalProps) => {
    useCreateEstimateModal();

    const { t } = useTranslation();

    const { data, isGetCurrencyLoading } = useGetCurrencyQuery();

    const [currencyListOptions, setCurrencyListOptions] = useState([]);

    const inputRef = useRef<HTMLInputElement>(null);

    const { control, handleSubmit, errors, setValue } = useHandleForm<IFormValues>({
        onSubmit: async (data) => {
            await createChild({ name: data.estimateName, currency: data.estimateNameCurrency });
        },
        defaultValues: {
            estimateName: t('CreateEstimateModal.useHandleForm.defaultValues.estimateName'),
        },
        resetEnable: false,
    });

    useEffect(() => {
        if (data) {
            const currencyList = data.map((currency) => ({
                value: currency.id,
                label: `${currency.title} (${currency.code}), ${currency.sign}`,
            }));
            setValue('estimateNameCurrency', 1);
            setCurrencyListOptions(currencyList);
        }
    }, [data]);

    return (
        <CustomModal
            isLoading={isGetCurrencyLoading}
            contentClasses={cls.modal}
            isOpen={isOpen}
            onHideModal={handleClose}
        >
            <CustomModal.Header>
                <Flex direction="column" align="start" gap={8}>
                    <span>{t('CreateEstimateModal.return.CustomModal.Header.span')}</span>
                    <UiText classes={cls.subHeader} variant="mediumPlain" tag="span">
                        {t('CreateEstimateModal.return.CustomModal.Header.UiText')}
                    </UiText>
                </Flex>
            </CustomModal.Header>
            <CustomModal.Body>
                <Form>
                    <Controller
                        name="estimateName"
                        control={control}
                        rules={{
                            required: t(
                                'CreateEstimateModal.return.CustomModal.Body.rules.required'
                            ),
                        }}
                        render={({ field }) => (
                            <Input
                                {...field}
                                maxLength={255}
                                error={errors.estimateName?.message}
                                ref={inputRef}
                                inputErrorClassName={errors.estimateName?.message && cls.InputError}
                                placeholder={t(
                                    'CreateEstimateModal.return.CustomModal.Body.Input.placeholder'
                                )}
                                autoFocus
                            />
                        )}
                    />
                    <Controller
                        key="estimateNameCurrency"
                        name="estimateNameCurrency"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                isSmartPosition
                                options={currencyListOptions}
                                id="estimateNameCurrencySelect"
                                needSearch={false}
                                classes={cls.SelectRoot}
                            />
                        )}
                    />
                </Form>
            </CustomModal.Body>
            <CustomModal.Footer>
                <UiButton btnText={t('button.create.text')} onClick={handleSubmit} />
            </CustomModal.Footer>
        </CustomModal>
    );
};
